import classNames from 'classnames';
import { PropsWithChildren, useMemo } from 'react';
import { Link, LinkProps } from 'react-router';

import { ExternalLink } from '../Forms/ExternalLink/ExternalLink';

import './Button.scss';

type LinkAsButtonProps = {
  isExternal?: boolean;
  title?: string;
  outline?: boolean;
  size?: 'sm' | 'md' | 'lg';
  bgColor?: 'blue' | 'sky-blue' | 'error';
  withIcon?: boolean;
  className?: string;
  isAnchor?: boolean;
  onClick?: () => void;
  isFormButton?: boolean;
  position?: 'left-side' | 'centered' | 'right-side';
} & LinkProps &
  PropsWithChildren;

export const LinkAsButton: React.FC<LinkAsButtonProps> = ({
  to,
  isExternal = false,
  title = '',
  outline = false,
  size = 'lg',
  bgColor = 'blue',
  withIcon = false,
  className = '',
  isAnchor = false,
  children,
  onClick,
  isFormButton = false,
  position,
  ...linkProps
}) => {
  const classList = useMemo(() => {
    return classNames(
      'link-as-button',
      {
        'link-as-button__outline': outline,
        [`link-as-button__${size}`]: size,
        'link-as-button__with-icon': withIcon,
        [`link-as-button__${bgColor}`]: bgColor,
        'link-as-button__form-button': isFormButton,
        [`link-as-button__form-button--${position}`]: isFormButton && position,
      },
      className,
    );
  }, [bgColor, className, withIcon, outline, size, isFormButton, position]);

  const onExternalClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(to.toString(), '_blank');
    onClick?.();
  };

  const inner = useMemo(() => {
    return children || <span className="align-text-top">{title}</span>;
  }, [children, title]);

  return isExternal ? (
    <ExternalLink {...linkProps} href={to.toString()} className={classList} onClick={onExternalClick}>
      {inner}
    </ExternalLink>
  ) : (
    <Link {...linkProps} to={to} className={classList} preventScrollReset={isAnchor} aria-label={title} role="button">
      {inner}
    </Link>
  );
};
